import { useEffect, useState } from "react";
import { ArrowRight, TrendDown, Barbell, HandHeart, UsersThree, Quotes, Star } from "@phosphor-icons/react";

const HIGHLIGHTS_URL = "https://instagram.com/sweatwithslay";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const wins = [
  {
    id: "recomp",
    icon: Barbell,
    headline: "8-week recomposition",
    stat: "−1.2% BF · +2 lb lean muscle · −4.25\"",
    body: "Dropped body fat, built muscle, and achieved visible recomposition — leaner and stronger in 8 weeks. Shoulders +2.5\" (muscle gain), waist −1.5\" (fat loss).",
    tag: "Body recomp",
  },
  {
    id: "shred",
    icon: TrendDown,
    headline: "Major shred — 16 lbs down",
    stat: "−16 lb · −3.6% BF · −15.25\"",
    body: "Down 16 pounds and 15.25 total inches in 8 weeks. Chest & shoulders −6.5\", waist −3.25\", hips, arms and legs all tighter. Full-body transformation.",
    tag: "Fat loss",
  },
  {
    id: "comeback",
    icon: HandHeart,
    headline: "60-year-old comeback",
    stat: "−24 lb · post back-surgery",
    body: "2x/week client, age 60. Back surgery and multiple injury limitations, plus a recent loss in the family — and still shows up. From 192 to 167 lbs. The results speak for themselves.",
    tag: "Resilience",
  },
  {
    id: "brothers",
    icon: UsersThree,
    headline: "Two brothers, one program",
    stat: "−14 lb · −19.5\" · −4.8% BF combined",
    body: "1x/week training over 8 weeks. They changed their eating, sleeping, and trained 3–4x/week with my programming. Two different goals, one plan — one gained lean muscle, one cut fat.",
    tag: "8-week sprint",
  },
];

export default function Testimonials() {
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND}/api/testimonials`)
      .then((r) => r.ok ? r.json() : { testimonials: [] })
      .then((d) => setQuotes((d.testimonials || []).slice(0, 6)))
      .catch(() => setQuotes([]));
  }, []);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-black py-16 lg:py-20 border-b border-white/10 overflow-hidden px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#FF1B91]" />
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">Real Wins / 05</p>
          <span className="h-px w-10 bg-[#FF1B91]" />
        </div>
        <h2 className="text-center font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.88] tracking-tighter mb-6">
          Real clients.{" "}
          <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">real wins.</span>
        </h2>
        <p className="text-center text-white/70 leading-relaxed max-w-xl mx-auto mb-14">
          A few recent client transformations — pulled straight from the receipts. More wins live on my Instagram highlights.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {wins.map((w) => {
            const Icon = w.icon;
            return (
              <div
                key={w.id}
                data-testid={`win-${w.id}`}
                className="group relative border border-white/10 bg-[#0F0F0F] hover:border-[#FF1B91] hover:bg-[#141414] transition-all p-7 lg:p-9 flex flex-col"
              >
                <span className="absolute top-5 right-5 text-[9px] uppercase tracking-[0.25em] bg-[#FF1B91]/15 text-[#FF1B91] border border-[#FF1B91]/30 px-2.5 py-1 font-bold">
                  {w.tag}
                </span>

                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 border border-white/15 flex items-center justify-center text-[#FF1B91] group-hover:border-[#FF1B91] transition-colors">
                    <Icon size={22} weight="bold" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 font-semibold">
                    Client Result
                  </div>
                </div>

                <h3 className="font-body font-semibold text-2xl lg:text-[26px] tracking-tight mb-4 leading-snug">
                  {w.headline}
                </h3>

                <div className="font-body font-bold text-[#FF1B91] text-lg lg:text-xl tracking-tight mb-5 leading-snug">
                  {w.stat}
                </div>

                <p className="text-white/75 leading-relaxed text-[15px]">
                  {w.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Client quotes — pulled from approved /testimonial submissions */}
        {quotes.length > 0 && (
          <div data-testid="client-quotes" className="border-t border-white/10 pt-14 mb-12">
            <p className="text-center text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold mb-8">/ In their own words</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quotes.map((q) => (
                <figure key={q.id} data-testid={`quote-${q.id}`} className="border border-white/10 bg-[#0F0F0F] hover:border-[#FF1B91]/60 transition-colors overflow-hidden">
                  {q.photo_url && (
                    <img
                      src={`${BACKEND}${q.photo_url}`}
                      alt={`${q.name} progress`}
                      data-testid={`quote-photo-${q.id}`}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover border-b border-white/10"
                    />
                  )}
                  <div className="p-6">
                    <Quotes size={20} weight="fill" className="text-[#FF1B91] mb-4" />
                    <blockquote className="text-white/85 text-base leading-snug whitespace-pre-wrap">{q.quote}</blockquote>
                    {q.what_changed && (
                      <>
                        <p className="text-[#FF1B91] uppercase tracking-[0.3em] text-[10px] font-bold mt-5 mb-2">What changed</p>
                        <p className="text-white/80 text-sm leading-snug whitespace-pre-wrap">{q.what_changed}</p>
                      </>
                    )}
                    {q.recommendation && (
                      <>
                        <p className="text-[#FF1B91] uppercase tracking-[0.3em] text-[10px] font-bold mt-5 mb-2">Would recommend because</p>
                        <p className="text-white/80 text-sm leading-snug whitespace-pre-wrap">{q.recommendation}</p>
                      </>
                    )}
                    <div className="mt-5" />
                    <figcaption className="flex items-center justify-between">
                      <div>
                        <p className="font-display font-black uppercase tracking-tighter text-base text-white">{q.name}</p>
                        <p className="text-white/55 text-[11px] uppercase tracking-[0.2em] font-bold mt-0.5">
                          {q.location || "Sweat With Slay client"}{q.duration ? ` · ${q.duration}` : ""}
                        </p>
                      </div>
                      <div className="flex gap-0.5 text-[#FF1B91]">
                        {[0,1,2,3,4].map((i) => <Star key={i} size={11} weight="fill" />)}
                      </div>
                    </figcaption>
                  </div>
                </figure>
              ))}
            </div>
            <p className="text-center mt-8">
              <a
                href="/testimonial"
                data-testid="testimonials-submit-cta"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF1B91] hover:text-white border-b border-[#FF1B91] hover:border-white pb-0.5"
              >
                Share your win → submit a testimonial
              </a>
            </p>
          </div>
        )}

        <div className="text-center flex flex-wrap gap-3 items-center justify-center">
          <a
            href={HIGHLIGHTS_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="testimonials-highlights-cta"
            className="inline-flex items-center gap-3 border border-white/30 hover:border-[#FF1B91] hover:text-[#FF1B91] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
          >
            See more wins on Instagram <ArrowRight size={16} weight="bold" />
          </a>
          {quotes.length === 0 && (
            <a
              href="/testimonial"
              data-testid="testimonials-share-cta"
              className="inline-flex items-center gap-3 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.2em]"
            >
              Share your win
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
